(() => {
  if (window.ShopDecorCartUpsellV7) return;
  window.ShopDecorCartUpsellV7 = true;

  const ROOT_ID = 'ShopDecorCartUpsell';
  let renderTimer = null;
  let rendering = false;
  let pendingRender = false;
  let lastCartSignature = '';

  const getRoot = () => document.getElementById(ROOT_ID);

  const scheduleRender = () => {
    window.clearTimeout(renderTimer);
    renderTimer = window.setTimeout(renderUpsell, 30);
  };

  const getCartProductIds = (root) =>
    Array.from(
      new Set(
        String(root.dataset.cartProductIds || '')
          .split(',')
          .map((id) => id.trim())
          .filter(Boolean)
      )
    );

  const fetchRecommendationCards = async (root, productId) => {
    if (!productId) return [];

    const url = new URL(root.dataset.recommendationsUrl, window.location.origin);
    url.searchParams.set('product_id', productId);
    url.searchParams.set('section_id', root.dataset.sectionId);
    url.searchParams.set('limit', root.dataset.candidateLimit || '4');
    url.searchParams.set('intent', 'related');

    const response = await fetch(url.toString(), {
      credentials: 'same-origin',
      headers: { Accept: 'text/html' },
    });
    if (!response.ok) return [];

    const html = await response.text();
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    return Array.from(parsed.querySelectorAll('.sd-upsell-card'));
  };

  const syncVariant = (card) => {
    const select = card.querySelector('[data-sd-upsell-variant]');
    const variantInput = card.querySelector('[data-sd-upsell-variant-input]');
    const quantityInput = card.querySelector('[data-sd-upsell-quantity-input]');

    if (!select) return;

    const option = select.selectedOptions[0];
    if (!option) return;

    if (variantInput) variantInput.value = option.value;
    if (quantityInput) quantityInput.value = option.dataset.addQuantity || '1';

    const price = card.querySelector('[data-sd-upsell-price]');
    if (price && option.dataset.price) price.textContent = option.dataset.price;

    const comparePrice = card.querySelector('[data-sd-upsell-compare-price]');
    if (comparePrice) {
      if (option.dataset.comparePrice) {
        comparePrice.textContent = option.dataset.comparePrice;
        comparePrice.hidden = false;
      } else {
        comparePrice.textContent = '';
        comparePrice.hidden = true;
      }
    }

    const image = card.querySelector('.sd-upsell-card__image');
    if (image && option.dataset.imageUrl) {
      image.removeAttribute('srcset');
      image.src = option.dataset.imageUrl;
    }
  };

  const bindCard = (card) => {
    if (card.dataset.sdUpsellBound === 'true') return;
    card.dataset.sdUpsellBound = 'true';

    const select = card.querySelector('[data-sd-upsell-variant]');
    if (select) {
      select.addEventListener('change', () => syncVariant(card));
      syncVariant(card);
    }
  };

  const addUniqueCards = (cards, seen, candidates, maxProducts) => {
    for (const card of cards) {
      const productId = String(card.dataset.productId || '');
      if (!productId || seen.has(productId)) continue;

      seen.add(productId);
      candidates.push(card);
      if (candidates.length >= maxProducts) break;
    }
  };

  const paintCards = (root, list, candidates, maxProducts, signature) => {
    list.replaceChildren();
    candidates.slice(0, maxProducts).forEach((card) => {
      bindCard(card);
      list.appendChild(card);
    });

    root.hidden = candidates.length === 0;
    root.dataset.rendered = 'true';
    lastCartSignature = signature;
  };

  async function renderUpsell() {
    if (rendering) {
      pendingRender = true;
      return;
    }

    const root = getRoot();
    if (!root) return;

    rendering = true;

    try {
      const list = root.querySelector('[data-sd-upsell-list]');
      if (!list) return;

      const cartProductIds = getCartProductIds(root);
      if (!cartProductIds.length) {
        list.replaceChildren();
        root.hidden = true;
        root.dataset.rendered = 'false';
        lastCartSignature = '';
        return;
      }

      const signature = root.dataset.cartSignature || cartProductIds.join('|');
      if (root.dataset.rendered === 'true' && signature === lastCartSignature) return;

      const maxProducts = Math.max(1, Number(root.dataset.maxProducts) || 2);
      const seen = new Set(cartProductIds);
      const candidates = [];
      const automaticSources = [];

      root.setAttribute('aria-busy', 'true');

      for (const productId of cartProductIds) {
        const manual = Array.from(root.querySelectorAll('template[data-sd-manual-source]'))
          .find((source) => source.dataset.sdManualSource === productId);
        if (manual && manual.dataset.hasManual === 'true') {
          if (candidates.length < maxProducts) {
            const cards = Array.from(manual.content.cloneNode(true).querySelectorAll('.sd-upsell-card'));
            addUniqueCards(cards, seen, candidates, maxProducts);
          }
        } else {
          automaticSources.push(productId);
        }
      }

      paintCards(root, list, candidates, maxProducts, signature);

      for (const productId of automaticSources) {
        if (candidates.length >= maxProducts) break;
        try {
          const cards = await fetchRecommendationCards(root, productId);
          if (root !== getRoot()) return;
          addUniqueCards(cards, seen, candidates, maxProducts);
          paintCards(root, list, candidates, maxProducts, signature);
        } catch (error) {
          console.warn('ShopDecor upsell: automatic recommendations request failed.', error);
        }
      }
    } catch (error) {
      console.warn('ShopDecor upsell could not be rendered.', error);
      const activeRoot = getRoot();
      if (activeRoot) activeRoot.hidden = true;
    } finally {
      const activeRoot = getRoot();
      if (activeRoot) activeRoot.removeAttribute('aria-busy');
      rendering = false;

      if (pendingRender) {
        pendingRender = false;
        scheduleRender();
      }
    }
  }

  const setupObserver = () => {
    const drawer = document.querySelector('cart-drawer');
    if (!drawer) return;

    const observer = new MutationObserver((mutations) => {
      const relevantMutation = mutations.some((mutation) => {
        const target = mutation.target;
        return !(target instanceof Element && target.closest(`#${ROOT_ID}`));
      });

      if (relevantMutation) scheduleRender();
    });

    observer.observe(drawer, { childList: true, subtree: true });
  };

  const boot = () => {
    setupObserver();
    scheduleRender();

    document.addEventListener('cart:update', scheduleRender);
    document.addEventListener('cart:refresh', scheduleRender);

    try {
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
        subscribe(PUB_SUB_EVENTS.cartUpdate, scheduleRender);
      }
    } catch (error) {
      // MutationObserver remains the fallback for Dawn cart updates.
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }
})();